import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';

// Página de DUPLO CHECK dos relatórios de fechamento:
// - aba "Pendentes": envios aguardando aprovação (aprovar/rejeitar = só admin)
// - aba "Clientes": seleciona quais clientes passam por duplo check
const RevisaoRelatorios = () => {
  const [abaSelecionada, setAbaSelecionada] = useState('pendentes');
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const [isAdmin, setIsAdmin] = useState(false);
  const [carregando, setCarregando] = useState(true);

  // Pendentes / revisados
  const [pendentes, setPendentes] = useState([]);
  const [revisados, setRevisados] = useState([]);
  const [decidindo, setDecidindo] = useState(null); // id em processamento

  // Prévia
  const [previewInfo, setPreviewInfo] = useState(null); // {html, assunto, destinatario, empresa}
  const [carregandoPreview, setCarregandoPreview] = useState(false);

  // Rejeição
  const [rejeitando, setRejeitando] = useState(null); // item sendo rejeitado
  const [motivoRejeicao, setMotivoRejeicao] = useState('');

  // Edição (correção pelo revisor antes de aprovar)
  const [editando, setEditando] = useState(null);   // item sendo editado
  const [formEdicao, setFormEdicao] = useState(null); // {dados_dre, mensagem_assessor, bancos, aplicacoes}

  // Configuração de clientes
  const [empresas, setEmpresas] = useState([]);
  const [clientesDuploCheck, setClientesDuploCheck] = useState(new Set());
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroRegime, setFiltroRegime] = useState('');
  const [salvandoCliente, setSalvandoCliente] = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access')}` }
  });

  const carregarPendentes = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/relatorios-pendentes/`,
        authHeaders()
      );
      if (res.data.success) {
        setPendentes(res.data.pendentes || []);
        setRevisados(res.data.revisados || []);
        setIsAdmin(!!res.data.is_admin);
      }
    } catch (error) {
      console.error('Erro ao carregar pendentes:', error);
      setMensagem('Erro ao carregar os envios pendentes.');
    }
  }, []);

  const carregarClientes = useCallback(async () => {
    try {
      const [resEmpresas, resDuplo] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASE}/api/empresas-contabil/`, authHeaders()),
        axios.get(`${process.env.REACT_APP_API_BASE}/api/duplo-check-clientes/`, authHeaders())
      ]);
      setEmpresas(resEmpresas.data.empresas || []);
      if (resDuplo.data.success) {
        setClientesDuploCheck(new Set((resDuplo.data.clientes || []).map(c => String(c.numero_dominio))));
        setIsAdmin(prev => prev || !!resDuplo.data.is_admin);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setMensagem('Erro ao carregar a lista de clientes.');
    }
  }, []);

  useEffect(() => {
    const carregarTudo = async () => {
      setCarregando(true);
      await Promise.all([carregarPendentes(), carregarClientes()]);
      setCarregando(false);
    };
    carregarTudo();
  }, [carregarPendentes, carregarClientes]);

  const alternarDuploCheck = async (empresa) => {
    if (!isAdmin) return;
    const nd = String(empresa.numero_dominio);
    const ativar = !clientesDuploCheck.has(nd);
    setSalvandoCliente(nd);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/duplo-check-clientes/`,
        { numero_dominio: nd, ativo: ativar },
        authHeaders()
      );
      if (res.data.success) {
        setClientesDuploCheck(prev => {
          const novo = new Set(prev);
          if (ativar) novo.add(nd); else novo.delete(nd);
          return novo;
        });
        setMensagem(ativar
          ? `Duplo check ATIVADO para ${empresa.empresa}`
          : `Duplo check desativado para ${empresa.empresa}`);
      }
    } catch (error) {
      setMensagem(error.response?.data?.erro || 'Erro ao alterar o duplo check.');
    } finally {
      setSalvandoCliente(null);
    }
  };

  const abrirPreview = async (item) => {
    setCarregandoPreview(true);
    setPreviewInfo({ item, empresa: item.empresa, html: '', assunto: '', destinatario: '' });
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/relatorios-pendentes/${item.id}/preview/`,
        authHeaders()
      );
      if (res.data.sucesso) {
        setPreviewInfo({
          item,
          empresa: item.empresa,
          html: res.data.html,
          assunto: res.data.assunto,
          destinatario: res.data.destinatario,
          editaveis: res.data.editaveis || null
        });
      } else {
        setMensagem(res.data.erro || 'Erro ao gerar a prévia.');
        setPreviewInfo(null);
      }
    } catch (error) {
      setMensagem(error.response?.data?.erro || 'Erro ao gerar a prévia.');
      setPreviewInfo(null);
    } finally {
      setCarregandoPreview(false);
    }
  };

  const decidir = async (item, acao, motivo = '') => {
    setDecidindo(item.id);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/relatorios-pendentes/${item.id}/decidir/`,
        { acao, motivo },
        authHeaders()
      );
      if (res.data.sucesso) {
        setMensagem(res.data.mensagem);
        setRejeitando(null);
        setMotivoRejeicao('');
        setPreviewInfo(null);
        await carregarPendentes();
        return true;
      }
      setMensagem(res.data.erro || 'Erro ao processar a decisão.');
      return false;
    } catch (error) {
      setMensagem(error.response?.data?.erro || 'Erro ao processar a decisão.');
      // 409 = a solicitação mudou de estado enquanto o admin revisava (reenvio
      // do operador ou outro aprovador). A lista precisa refletir isso na hora.
      if (error.response?.status === 409) {
        setPreviewInfo(null);
        setRejeitando(null);
        await carregarPendentes();
      }
      return false;
    } finally {
      setDecidindo(null);
    }
  };

  // Abre o editor com os valores atuais do e-mail represado
  const abrirEditor = (item, editaveis) => {
    if (!editaveis) return;
    setFormEdicao({
      dados_dre: (editaveis.dados_dre || []).map((l, i) => ({
        id: `${Date.now()}_${i}`,
        chave: l.chave || '',
        valor: l.valor ?? 0,
        negrito: !!l.negrito
      })),
      mensagem_assessor: editaveis.mensagem_assessor || '',
      bancos: editaveis.bancos ?? 0,
      aplicacoes: editaveis.aplicacoes ?? 0
    });
    setEditando(item);
    setPreviewInfo(null);
  };

  const fecharEditor = () => {
    setEditando(null);
    setFormEdicao(null);
  };

  // Salva as correções: aceita o envio e registra como "Aceito com ajustes"
  const salvarAjustesEAprovar = async () => {
    if (!editando || !formEdicao) return;
    const linhasDre = formEdicao.dados_dre
      .filter(l => String(l.chave).trim())
      .map(l => ({ chave: l.chave, valor: l.valor, negrito: l.negrito }));

    // O envio é irreversível: o que passar daqui vai para o cliente. DRE sem
    // linha nenhuma sairia com a tabela em branco, e valor ilegível viraria
    // R$ 0,00 no relatório — os dois são barrados antes do disparo.
    if (!linhasDre.length) {
      setMensagem('O DRE ficou sem nenhuma linha. Restaure as linhas antes de aprovar.');
      return;
    }
    const numeroInvalido = (v) => {
      if (typeof v === 'number') return !Number.isFinite(v);
      const t = String(v ?? '').trim();
      return !t || !Number.isFinite(Number(t.replace(/\./g, '').replace(',', '.')));
    };
    const linhaRuim = linhasDre.find(l => numeroInvalido(l.valor));
    if (linhaRuim) {
      setMensagem(`Valor inválido na linha "${linhaRuim.chave}" do DRE — corrija antes de aprovar.`);
      return;
    }
    if (numeroInvalido(formEdicao.bancos) || numeroInvalido(formEdicao.aplicacoes)) {
      setMensagem('Bancos e Aplicações precisam de um valor numérico — corrija antes de aprovar.');
      return;
    }

    const ajustes = {
      dados_dre: linhasDre,
      mensagem_assessor: formEdicao.mensagem_assessor,
      bancos: formEdicao.bancos,
      aplicacoes: formEdicao.aplicacoes
    };
    setDecidindo(editando.id);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/relatorios-pendentes/${editando.id}/decidir/`,
        { acao: 'aprovar_ajustado', ajustes },
        authHeaders()
      );
      if (res.data.sucesso) {
        setMensagem(res.data.mensagem);
        fecharEditor();
        await carregarPendentes();
      } else {
        setMensagem(res.data.erro || 'Erro ao salvar os ajustes.');
      }
    } catch (error) {
      setMensagem(error.response?.data?.erro || 'Erro ao salvar os ajustes.');
      if (error.response?.status === 409) {
        fecharEditor();
        await carregarPendentes();
      }
    } finally {
      setDecidindo(null);
    }
  };

  const atualizarLinhaDre = (id, campo, valor) => {
    setFormEdicao(prev => ({
      ...prev,
      dados_dre: prev.dados_dre.map(l => (l.id === id ? { ...l, [campo]: valor } : l))
    }));
  };

  const regimesDisponiveis = [...new Set(empresas.map(e => e.regime).filter(Boolean))].sort();

  const empresasFiltradas = empresas.filter(emp => {
    if (filtroRegime && (emp.regime || '') !== filtroRegime) return false;
    if (!filtroCliente) return true;
    const f = filtroCliente.toLowerCase();
    return (emp.empresa || '').toLowerCase().includes(f) ||
           String(emp.numero_dominio || '').includes(f) ||
           (emp.operador || '').toLowerCase().includes(f);
  });

  const badgeStatus = (status) => {
    if (status === 'APROVADO') return <span className="badge bg-success">Aprovado e enviado</span>;
    if (status === 'APROVADO_AJUSTADO') return <span className="badge bg-success">Aceito com ajustes</span>;
    if (status === 'REJEITADO') return <span className="badge bg-danger">Rejeitado</span>;
    // o operador reenviou o mesmo cliente/competência: esta solicitação foi
    // encerrada e uma nova entrou na fila (o payload revisado nunca é alterado)
    if (status === 'SUBSTITUIDO') return <span className="badge bg-secondary">Substituído por reenvio</span>;
    if (status === 'PROCESSANDO') return <span className="badge bg-info text-dark">Enviando...</span>;
    return <span className="badge bg-warning text-dark">Pendente</span>;
  };

  if (carregando) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3">Carregando revisão de relatórios...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4 px-4">
      <div className="shadow p-4 rounded bg-white">
        {/* Cabeçalho no padrão do app */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <i className="fas fa-user-shield header-icon fs-1 me-3"></i>
            <div>
              <h2 className="titulo-pagina m-0">Revisão de Relatórios (Duplo Check)</h2>
              <p className="text-muted mb-0">
                Fechamentos represados para aprovação antes do envio ao cliente
              </p>
            </div>
          </div>
          {!isAdmin && (
            <span className="badge bg-secondary p-2">
              <i className="fas fa-eye me-1"></i>
              Somente visualização — aprovação restrita a administradores
            </span>
          )}
        </div>

        {/* Abas */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <a
              href="#pendentes"
              className={`nav-link ${abaSelecionada === 'pendentes' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); setAbaSelecionada('pendentes'); }}
            >
              <i className="fas fa-hourglass-half me-2"></i>
              Pendentes de Aprovação
              {pendentes.length > 0 && (
                <span className="badge bg-danger ms-2">{pendentes.length}</span>
              )}
            </a>
          </li>
          <li className="nav-item">
            <a
              href="#clientes"
              className={`nav-link ${abaSelecionada === 'clientes' ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); setAbaSelecionada('clientes'); }}
            >
              <i className="fas fa-users-cog me-2"></i>
              Clientes com Duplo Check
              <span className="badge bg-primary ms-2">{clientesDuploCheck.size}</span>
            </a>
          </li>
        </ul>

        {/* ABA PENDENTES */}
        {abaSelecionada === 'pendentes' && (
          <>
            {pendentes.length === 0 ? (
              <div className="alert alert-success text-center">
                <i className="fas fa-check-circle me-2"></i>
                Nenhum envio aguardando aprovação.
              </div>
            ) : (
              <div className="table-responsive mb-4">
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Empresa</th>
                      <th>Nº Domínio</th>
                      <th>Competência</th>
                      <th>Destinatário</th>
                      <th>Solicitado por</th>
                      <th>Solicitado em</th>
                      <th style={{ width: '250px' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendentes.map(item => (
                      <tr key={item.id}>
                        <td className="fw-bold">{item.empresa}</td>
                        <td>{item.numero_dominio}</td>
                        <td><span className="badge bg-dark">{item.mes_ano}</span></td>
                        <td className="small">{item.destinatario}</td>
                        <td>{item.solicitado_por || '-'}</td>
                        <td className="small">{item.criado_em}</td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => abrirPreview(item)}
                              title="Ver o e-mail exatamente como será enviado"
                            >
                              <i className="fas fa-eye me-1"></i>Prévia
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => { setRejeitando(item); setMotivoRejeicao(''); }}
                              disabled={!isAdmin || decidindo === item.id}
                              title={isAdmin ? 'Rejeitar (o operador deverá corrigir e reenviar)' : 'Somente administradores podem rejeitar'}
                            >
                              <i className="fas fa-times me-1"></i>Rejeitar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Histórico de revisões */}
            {revisados.length > 0 && (
              <>
                <h6 className="fw-bold mb-2">
                  <i className="fas fa-history me-2"></i>
                  Últimas revisões
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Empresa</th>
                        <th>Competência</th>
                        <th>Solicitado por</th>
                        <th>Status</th>
                        <th>Revisado por</th>
                        <th>Revisado em</th>
                        <th>Observação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {revisados.map(item => (
                        <tr key={item.id}>
                          <td>{item.empresa}</td>
                          <td>{item.mes_ano}</td>
                          <td>{item.solicitado_por || '-'}</td>
                          <td>{badgeStatus(item.status)}</td>
                          <td>{item.revisado_por || '-'}</td>
                          <td className="small">{item.revisado_em || '-'}</td>
                          <td className="small">{item.motivo_rejeicao || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {/* ABA CLIENTES */}
        {abaSelecionada === 'clientes' && (
          <>
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Clientes marcados aqui têm o envio do fechamento <strong>represado para aprovação
              de um administrador</strong>. Os demais disparam normalmente.
              {!isAdmin && <span className="d-block mt-1 text-muted">Somente administradores podem alterar a marcação.</span>}
            </div>

            <div className="d-flex gap-2 flex-wrap mb-3">
              <div className="input-group" style={{ maxWidth: '420px' }}>
                <span className="input-group-text"><i className="fas fa-search"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por empresa, domínio ou operador..."
                  value={filtroCliente}
                  onChange={(e) => setFiltroCliente(e.target.value)}
                />
              </div>
              <div className="input-group" style={{ maxWidth: '300px' }}>
                <span className="input-group-text"><i className="fas fa-balance-scale"></i></span>
                <select
                  className="form-select"
                  value={filtroRegime}
                  onChange={(e) => setFiltroRegime(e.target.value)}
                  title="Filtrar por regime tributário"
                >
                  <option value="">Todos os regimes</option>
                  {regimesDisponiveis.map(regime => (
                    <option key={regime} value={regime}>{regime}</option>
                  ))}
                </select>
              </div>
              {(filtroCliente || filtroRegime) && (
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => { setFiltroCliente(''); setFiltroRegime(''); }}
                  title="Limpar filtros"
                >
                  <i className="fas fa-times me-1"></i>Limpar
                </button>
              )}
            </div>

            <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{ width: '130px' }}>Duplo Check</th>
                    <th>Nº Domínio</th>
                    <th>Empresa</th>
                    <th>Operador</th>
                    <th>Regime</th>
                  </tr>
                </thead>
                <tbody>
                  {empresasFiltradas.map(emp => {
                    const nd = String(emp.numero_dominio);
                    const ativo = clientesDuploCheck.has(nd);
                    return (
                      <tr key={nd} className={ativo ? 'table-info' : ''}>
                        <td className="text-center">
                          {salvandoCliente === nd ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <div className="form-check form-switch d-flex justify-content-center">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={ativo}
                                onChange={() => alternarDuploCheck(emp)}
                                disabled={!isAdmin}
                                style={{ cursor: isAdmin ? 'pointer' : 'not-allowed' }}
                                title={isAdmin ? '' : 'Somente administradores podem alterar'}
                              />
                            </div>
                          )}
                        </td>
                        <td>{emp.numero_dominio}</td>
                        <td className={ativo ? 'fw-bold' : ''}>{emp.empresa}</td>
                        <td>{emp.operador || '-'}</td>
                        <td>{emp.regime || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-end text-muted small">
              {empresasFiltradas.length} cliente(s) listado(s) — {clientesDuploCheck.size} com duplo check ativo
            </div>
          </>
        )}
      </div>

      {/* MODAL DE PRÉVIA */}
      {previewInfo && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-eye me-2"></i>
                  Prévia do E-mail — {previewInfo.empresa}
                </h5>
                <button type="button" className="btn-close" onClick={() => setPreviewInfo(null)}></button>
              </div>
              <div className="modal-body" style={{ background: '#f0f2f5' }}>
                {previewInfo.destinatario && (
                  <div className="mb-3 p-3 rounded bg-white border">
                    <div className="mb-1">
                      <span className="text-muted" style={{ width: '80px', display: 'inline-block' }}>Para:</span>
                      <strong>{previewInfo.destinatario}</strong>
                    </div>
                    <div>
                      <span className="text-muted" style={{ width: '80px', display: 'inline-block' }}>Assunto:</span>
                      <strong>{previewInfo.assunto}</strong>
                    </div>
                  </div>
                )}
                {carregandoPreview ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
                    <p className="mt-3 text-muted">Gerando prévia do e-mail...</p>
                  </div>
                ) : (
                  <iframe
                    title="Prévia do e-mail pendente"
                    srcDoc={previewInfo.html}
                    sandbox="allow-same-origin"
                    style={{ width: '100%', height: '65vh', border: '1px solid #e7e9ee', borderRadius: '8px', background: '#ffffff' }}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPreviewInfo(null)}>
                  <i className="fas fa-times me-2"></i>Fechar
                </button>
                {previewInfo.item?.status === 'PENDENTE' && (
                  <>
                    <button
                      type="button"
                      className="btn btn-outline-warning"
                      onClick={() => abrirEditor(previewInfo.item, previewInfo.editaveis)}
                      disabled={!isAdmin || carregandoPreview || !previewInfo.editaveis}
                      title={isAdmin
                        ? 'Corrigir valores do DRE, mensagem ou posição financeira antes de aprovar'
                        : 'Somente administradores podem editar'}
                    >
                      <i className="fas fa-pen me-2"></i>Editar
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => { setRejeitando(previewInfo.item); setMotivoRejeicao(''); setPreviewInfo(null); }}
                      disabled={!isAdmin || decidindo === previewInfo.item?.id}
                      title={isAdmin ? 'Rejeitar (o operador deverá corrigir e reenviar)' : 'Somente administradores podem rejeitar'}
                    >
                      <i className="fas fa-times me-2"></i>Rejeitar
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={async () => {
                        const ok = await decidir(previewInfo.item, 'aprovar');
                        if (ok) setPreviewInfo(null);
                      }}
                      disabled={!isAdmin || carregandoPreview || decidindo === previewInfo.item?.id}
                      title={isAdmin ? 'Aprovar e disparar o e-mail ao cliente' : 'Somente administradores podem aprovar'}
                    >
                      {decidindo === previewInfo.item?.id ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-2"></i>Aprovar e Enviar
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EDIÇÃO (correção do revisor antes de aprovar) */}
      {editando && formEdicao && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-pen me-2"></i>
                  Corrigir antes de aprovar — {editando.empresa} ({editando.mes_ano})
                </h5>
                <button type="button" className="btn-close" onClick={fecharEditor} disabled={decidindo === editando.id}></button>
              </div>

              <div className="modal-body">
                <div className="alert alert-warning py-2 small">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Ao salvar, o relatório será <strong>aceito com os ajustes</strong> e enviado ao cliente
                  imediatamente. A alteração fica registrada no histórico.
                </div>

                {/* DRE */}
                <h6 className="fw-bold mb-2">
                  <i className="fas fa-table me-2"></i>DRE
                </h6>
                <div className="table-responsive mb-2">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Conta / Nomenclatura</th>
                        <th style={{ width: '180px' }}>Valor (R$)</th>
                        <th style={{ width: '90px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formEdicao.dados_dre.map(linha => (
                        <tr key={linha.id}>
                          <td className="p-1">
                            <input
                              type="text"
                              className="form-control form-control-sm border-0"
                              value={linha.chave}
                              onChange={(e) => atualizarLinhaDre(linha.id, 'chave', e.target.value)}
                              style={linha.negrito ? { fontWeight: 'bold' } : {}}
                            />
                          </td>
                          <td className="p-1">
                            <input
                              type="number"
                              step="0.01"
                              className="form-control form-control-sm border-0 text-end"
                              value={linha.valor}
                              onChange={(e) => atualizarLinhaDre(linha.id, 'valor', e.target.value)}
                              style={linha.negrito ? { fontWeight: 'bold' } : {}}
                            />
                          </td>
                          <td className="p-1 text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <button
                                className={`btn btn-sm py-0 px-1 ${linha.negrito ? 'btn-warning' : 'btn-outline-secondary'}`}
                                onClick={() => atualizarLinhaDre(linha.id, 'negrito', !linha.negrito)}
                                title={linha.negrito ? 'Remover negrito' : 'Negrito'}
                                style={{ fontWeight: 'bold', fontFamily: 'serif' }}
                              >
                                N
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger py-0 px-1"
                                onClick={() => setFormEdicao(prev => ({
                                  ...prev,
                                  dados_dre: prev.dados_dre.filter(l => l.id !== linha.id)
                                }))}
                                title="Remover linha"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {formEdicao.dados_dre.length === 0 && (
                        <tr>
                          <td colSpan="3" className="text-center text-muted small py-3">
                            Nenhuma linha de DRE neste relatório.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <button
                  className="btn btn-sm btn-success mb-4"
                  onClick={() => setFormEdicao(prev => ({
                    ...prev,
                    dados_dre: [...prev.dados_dre, { id: `novo_${Date.now()}`, chave: '', valor: 0, negrito: false }]
                  }))}
                >
                  <i className="fas fa-plus me-1"></i>Adicionar linha
                </button>

                {/* Posição financeira */}
                <h6 className="fw-bold mb-2">
                  <i className="fas fa-wallet me-2"></i>Posição Financeira
                </h6>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Bancos (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={formEdicao.bancos}
                      onChange={(e) => setFormEdicao(prev => ({ ...prev, bancos: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-bold">Aplicações (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={formEdicao.aplicacoes}
                      onChange={(e) => setFormEdicao(prev => ({ ...prev, aplicacoes: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Mensagem do assessor */}
                <h6 className="fw-bold mb-2">
                  <i className="fas fa-comment-dots me-2"></i>Mensagem do Assessor
                </h6>
                <textarea
                  className="form-control"
                  rows="10"
                  value={formEdicao.mensagem_assessor}
                  onChange={(e) => setFormEdicao(prev => ({ ...prev, mensagem_assessor: e.target.value }))}
                  placeholder="Sem mensagem do assessor neste relatório."
                ></textarea>
                <div className="form-text">
                  Use **texto** para negrito, como no editor do operador. Cada linha vira um parágrafo.
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={fecharEditor} disabled={decidindo === editando.id}>
                  <i className="fas fa-arrow-left me-2"></i>Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={salvarAjustesEAprovar}
                  disabled={!isAdmin || decidindo === editando.id}
                  title="Salvar as correções, aceitar e enviar ao cliente"
                >
                  {decidindo === editando.id ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Salvando e enviando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>Salvar e Aceitar com Ajustes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE REJEIÇÃO */}
      {rejeitando && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-times-circle me-2 text-danger"></i>
                  Rejeitar Envio — {rejeitando.empresa}
                </h5>
                <button type="button" className="btn-close" onClick={() => setRejeitando(null)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small">
                  O envio será marcado como rejeitado e <strong>não</strong> será disparado ao cliente.
                  O operador ({rejeitando.solicitado_por || 'não identificado'}) deverá corrigir e reenviar.
                </p>
                <label className="form-label fw-bold">Motivo da rejeição</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder="Descreva o que precisa ser corrigido..."
                  value={motivoRejeicao}
                  onChange={(e) => setMotivoRejeicao(e.target.value)}
                ></textarea>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setRejeitando(null)}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => decidir(rejeitando, 'rejeitar', motivoRejeicao)}
                  disabled={!motivoRejeicao.trim() || decidindo === rejeitando.id}
                >
                  {decidindo === rejeitando.id
                    ? <span className="spinner-border spinner-border-sm me-2"></span>
                    : <i className="fas fa-times me-2"></i>}
                  Confirmar Rejeição
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevisaoRelatorios;
